/**
 * HTTP client.
 *
 * Two decisions worth stating explicitly:
 *
 * 1. The access token lives in a module-scoped variable — **in memory only**.
 *    Never localStorage or sessionStorage: those survive a tab close, so an XSS
 *    would yield a durable credential rather than a 15-minute one. The refresh
 *    token is an HttpOnly cookie the JavaScript here can never read.
 *
 * 2. A 401 triggers exactly one in-flight refresh. Concurrent requests queue
 *    behind that single promise instead of each firing their own refresh, which
 *    would rotate the token repeatedly and trip the backend's reuse detection —
 *    logging the user out for doing nothing wrong.
 *
 * docs/04-auth-authz.md §4.4, docs/07-frontend.md §11
 */

import { ApiError, NetworkError, type ProblemDetail } from "./errors";

const API_BASE = "/api/v1";

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when the session cannot be recovered, so the app can route to login. */
export function setAuthLostHandler(handler: (() => void) | null): void {
  onAuthLost = handler;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip the automatic refresh-and-retry. Used by the auth calls themselves. */
  skipAuthRetry?: boolean;
  /**
   * What the server is expected to send back. Documents — receipts, transfer
   * certificates, payslips — come back as PDF bytes, and they cannot be plain
   * links: the route needs the Authorization header, and an anchor sends none.
   */
  expect?: "json" | "blob";
}

async function parseProblem(response: Response): Promise<ProblemDetail> {
  try {
    const body = (await response.json()) as ProblemDetail;
    if (typeof body?.code === "string") return body;
    return synthesiseProblem(response);
  } catch {
    return synthesiseProblem(response);
  }
}

function synthesiseProblem(response: Response): ProblemDetail {
  return {
    type: "about:blank",
    title: response.statusText || "Request failed",
    status: response.status,
    code: `http.${response.status}`,
    detail: response.statusText || "The request failed.",
    instance: response.url,
  };
}

async function performRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) return false;

    const body = (await response.json()) as { access_token: string };
    accessToken = body.access_token;
    return true;
  } catch {
    return false;
  }
}

/** Single-flight: concurrent 401s share one refresh attempt. */
function refreshSession(): Promise<boolean> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function send<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuthRetry, headers, expect = "json", ...rest } = options;

  const requestHeaders = new Headers(headers);
  if (body !== undefined && !(body instanceof FormData)) {
    requestHeaders.set("Content-Type", "application/json");
  }
  if (accessToken) {
    requestHeaders.set("Authorization", `Bearer ${accessToken}`);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...rest,
      headers: requestHeaders,
      // Carries the HttpOnly refresh cookie on the auth endpoints. Same-origin
      // in both dev (Vite proxy) and production (shared load balancer).
      credentials: "include",
      body:
        body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (response.status === 401 && !skipAuthRetry) {
    const recovered = await refreshSession();
    if (recovered) {
      return send<T>(path, { ...options, skipAuthRetry: true });
    }
    accessToken = null;
    onAuthLost?.();
  }

  if (!response.ok) {
    throw new ApiError(await parseProblem(response));
  }

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  if (expect === "blob") {
    return (await response.blob()) as T;
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => send<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>(path, { ...options, method: "PATCH", body }),
  /** For replace-the-whole-set writes, such as a user's roles. */
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    send<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    send<T>(path, { ...options, method: "DELETE" }),
  /**
   * Fetch a PDF and hand back an object URL, plus the revoke to call when the
   * caller is done with it. Leaking these leaks the whole document in memory.
   */
  document: async (path: string) => {
    const blob = await send<Blob>(path, { method: "GET", expect: "blob" });
    return URL.createObjectURL(blob);
  },
  /** Bootstrap the session from the refresh cookie on a page load. */
  restoreSession: refreshSession,
};
